"use client";

import React from "react";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Shield, Zap } from "lucide-react";
const milestones = [{
  year: "1921",
  title: "The First Wrench Turned",
  desc: "Dott's Garage opens its doors in Albany. A humble single-bay shop dedicated to the burgeoning era of the horseless carriage and local trust."
}, {
  year: "1950",
  title: "Post-War Power",
  desc: "Expanding to meet the golden age of American steel. We became the go-to experts for V8 engines and the complex transmissions of the fifties."
}, {
  year: "1980",
  title: "The Electronic Shift",
  desc: "As fuel injection and early computers entered the engine bay, our mechanics led the way in technical training and precision diagnostics."
}, {
  year: "2021",
  title: "A Century of Service",
  desc: "Celebrating 100 years of excellence. Today, we combine vintage craftsmanship with state-of-the-art digital scanning for modern performance."
}];
function TimelineItem({
  item,
  index,
  isLast
}: {
  item: (typeof milestones)[0];
  index: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.4,
    once: true
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className="relative w-full mb-16 md:mb-32 last:mb-0 group">
      {}
      {!isLast && <div className="absolute left-6 md:left-1/2 top-12 md:top-1/2 bottom-[-4rem] md:bottom-[-8rem] w-1 -translate-x-1/2 bg-border overflow-hidden rounded-full">
          <div className={cn("w-full h-full bg-gradient-to-b from-primary via-primary to-primary origin-top transition-transform duration-1000 ease-out delay-300", isVisible ? "scale-y-100" : "scale-y-0")} />
        </div>}

      {}
      <div className={cn("absolute left-6 md:left-1/2 top-8 md:top-1/2 -translate-x-1/2 md:-translate-y-1/2 w-8 h-8 rounded-full border-4 z-10 transition-all duration-500 flex items-center justify-center", isVisible ? "border-primary bg-background ring-4 ring-primary/20 shadow-lg" : "border-border bg-muted")}>
        <div className={cn("w-2.5 h-2.5 rounded-full transition-colors duration-500 delay-200", isVisible ? "bg-primary shadow-sm" : "bg-transparent")} />
      </div>

      {}
      <div className={cn("relative w-full md:w-1/2 pl-20 md:pl-0", isEven ? "md:pr-20 md:ml-0 md:text-right" : "md:pl-20 md:ml-auto md:text-left")}>
        <Card className={cn("transition-all duration-700 relative overflow-hidden border-[3px]", isVisible ? "border-primary/40 shadow-xl translate-y-0 opacity-100" : "border-transparent shadow-none translate-y-12 opacity-0")}>
          {}
          <div className={cn("absolute inset-0 bg-gradient-to-br from-black/40 via-black/20 to-transparent opacity-0 transition-opacity duration-700 pointer-events-none", isVisible && "opacity-100")} />
          <CardHeader className="pb-2">
            <CardTitle className="text-7xl md:text-8xl font-black font-default tracking-tighter uppercase">
              <span className={cn("transition-colors duration-700", isVisible ? "text-primary" : "text-foreground")}>
                {item.year}
              </span>
            </CardTitle>
            <div className="text-2xl font-semibold font-default mt-2 text-foreground tracking-tight">
              {item.title}
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-lg leading-relaxed font-default">
              {item.desc}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function OurLocalLegacy() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  return <section data-section-id="246412" id="a-century-under-the-hood" className="dark relative bg-background py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660796/img/blueprint_grid.png')] bg-repeat" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div ref={headerRef} className={cn("max-w-3xl mx-auto text-center mb-20 transition-all duration-700", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="inline-flex items-center justify-center p-3 bg-primary/20 rounded-md mb-6">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold font-default tracking-tighter uppercase text-foreground mb-6">
            A Century <span className="text-primary">Under The Hood</span>
          </h2>
          <p className="text-xl text-muted-foreground font-default">Since 1921, Dott's Garage has been Albany's trusted name in automotive care. Four generations of mechanics, one unwavering commitment to quality.</p>
        </div>

        {}
        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="absolute left-6 md:left-1/2 top-0 h-12 md:h-1/2 w-1 -translate-x-1/2 bg-gradient-to-b from-transparent to-border" />

          {milestones.map((item, index) => <TimelineItem key={index} item={item} index={index} isLast={index === milestones.length - 1} />)}
        </div>

        {}
        <div className="mt-24 text-center relative z-10">
          <Button size="lg" asChild className="rounded-md px-8 h-14 text-lg bg-primary hover:bg-primary/90 text-primary-foreground transition-all shadow-lg">
            <Link href="/BookAppointmentPage">
              <Zap className="w-5 h-5 mr-2" />
              Book Your Century-Grade Service
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}